import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

import { Config, Translation, TranslationNode } from "@/types/nango"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function t(
  translation: Translation,
  config: Config,
  key: string,
): string {
  const entry = translation[key]
  if (entry) return entry[config.LOCALE] ?? entry.FR
  throw new Error("Your translation is missing a key: " + key)
}

export function formatTimeString(time: string | null | undefined) {
  if (time === null || time === undefined) {
    return ""
  }
  const [hours, minutes, secondsWithMilliseconds] = time.split(":")
  const [seconds, microseconds] = secondsWithMilliseconds.split(".")

  const h = parseInt(hours, 10)
  const m = parseInt(minutes, 10)
  const s = parseInt(seconds, 10)
  const ms = parseInt(microseconds ? microseconds.slice(0, 3) : "0", 10)

  const parts = []
  if (h > 0) parts.push(`${h}h`)
  if (m > 0 || h > 0) parts.push(`${m}m`)
  parts.push(`${(s + ms / 1000).toFixed(2)}s`)

  return parts.join(" ")
}

export function tN(
  translation: TranslationNode,
  config: Config,
  key: string,
): React.ReactNode {
  const entry = translation[key]
  if (entry) return entry[config.LOCALE] ?? entry.FR
  throw new Error("Your translation is missing a key: " + key)
}

export function isRunningInE2E(): boolean {
  // Check environment variable (works on server and client)
  if (process.env.IS_E2E_TEST === "true") {
    return true
  }

  // Check for Cypress on client side
  if (typeof window !== "undefined") {
    const w = window as unknown as {
      Cypress?: unknown
      __DISABLE_UMAMI__?: boolean
    }
    return Boolean(w.Cypress || w.__DISABLE_UMAMI__)
  }

  return false
}

// Shared text tones for the shadcn primitives. Components own their colour, so
// a page asks for a tone instead of writing a text-* class at the call site.
const TONE_CLASS = {
  muted: "text-muted-foreground",
  "muted-interactive": "text-muted-foreground hover:text-foreground",
  faint: "text-muted-foreground/50",
  strong: "text-foreground",
  success: "text-success",
  destructive: "text-destructive",
  primary: "text-primary",
} as const

export type Tone = keyof typeof TONE_CLASS

export function toneClass(tone?: Tone) {
  return tone ? TONE_CLASS[tone] : undefined
}
